import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../LoadingSpinner';

interface EmailVerificationGuardProps {
  children: React.ReactNode;
}

export const EmailVerificationGuard: React.FC<EmailVerificationGuardProps> = ({ children }) => {
  const { user, loading, isAuthenticated, refreshUserProfile } = useAuthContext();
  const navigate = useNavigate();
  const hasRefreshed = useRef(false);

  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      // If user is authenticated but email is not verified, try to refresh the profile first
      if (!user.emailVerified && !hasRefreshed.current) {
        hasRefreshed.current = true;
        // Force refresh the user profile to pick up any server-side changes
        refreshUserProfile().then(() => {
          // The refresh will update the auth state, which will trigger this effect again
          // We don't need to do anything here as the effect will re-run with the updated user
        }).catch(() => {
          // If refresh fails, proceed with redirect using current user
          const params = new URLSearchParams();
          params.set('email', user.email);
          navigate(`/verify-code?${params.toString()}`);
        });
      } else if (!user.emailVerified && hasRefreshed.current) {
        // If we've already tried refreshing and email is still not verified, redirect
        const params = new URLSearchParams();
        params.set('email', user.email);
        navigate(`/verify-code?${params.toString()}`);
      }
    }
  }, [user?.uid, user?.emailVerified, loading, isAuthenticated, navigate, refreshUserProfile]);

  // Show loading while checking authentication state
  if (loading) {
    return <LoadingSpinner />;
  }

  // If not authenticated, allow the child component to handle it
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // If authenticated but email not verified, show loading (will redirect)
  if (user && !user.emailVerified) {
    return <LoadingSpinner />;
  }

  // If authenticated and email verified, show children
  return <>{children}</>;
};
